<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-card class="donation-flow summary-view" :class="compact && 'elevation-0'" style="border-radius: 16px;">
    <div class="contribution-flow-container">
    <v-card-title :class="compact && 'text-gray-900'" class="d-flex justify-space-between align-center mb-6" style="padding: 0 !important; line-height: 1;">
      <span class="donation-flow__step-title">{{ t('donationGuide.summaryView.title') }}</span>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 3, total: 3 }) }}</span>
    </v-card-title>
    <v-card-subtitle :class="compact && 'pt-0 pl-0 pt-8 pb-4 pl-0 mb-6 summary-view-subtitle'" color="grey">
      {{ t('donationGuide.summaryView.subtitle') }}
    </v-card-subtitle>
    <v-card-text :class="compact && 'pt-0 pl-0 mt-8'">
      <v-form ref="form" v-model="valid" data-cy="summary-view-form" lazy-validation>
        <!-- Your gift card -->
        <div v-if="isMemoryGift" class="gift-card-summary mt-4">
          <p class="donation-flow__option-title mb-4" style="color: #4e5668 !important">
            {{ t('summary.giftCard.title') }}
          </p>

          <div
            class="gift-card-summary__card"
            data-cy="summary-view-gift-card"
            role="button"
            tabindex="0"
            :aria-label="t('aria.label.showPreview')"
            @click="openPreview"
            @keydown.enter="openPreview"
          >
            <!-- Swatch built from the gift card image's dominant colours -->
            <div class="gift-card-summary__swatch" :style="swatchStyle">
              <div class="gift-card-summary__swatch-inner" />
            </div>

            <!-- Title + contents -->
            <div class="gift-card-summary__body">
              <div class="gift-card-summary__title">
                {{ t('summary.giftCard.inMemoryOf', { name: giftCardRecipientName }) }}
              </div>
              <div class="gift-card-summary__contents">
                {{ giftCardContents }}
              </div>
            </div>

            <!-- Preview & edit action -->
            <div class="gift-card-summary__actions">
              <button
                type="button"
                class="gift-card-summary__action"
                data-cy="summary-view-gift-card-preview-btn"
                @click.stop="openPreview"
              >
                {{ t('summary.giftCard.previewAndEdit') }}
              </button>
            </div>
          </div>

          <v-alert
            v-model="previewError"
            border="start"
            type="error"
            class="mt-2"
            colored-border
            elevation="2"
          >
            {{ t('confirm.previewError') }}
          </v-alert>
        </div>

        <!-- Gift card preview modal -->
        <certificate-preview
          v-if="isMemoryGift"
          v-model="preview"
          :image-id="giftCardImageId"
          :image-url="giftCardImageUrl"
          :image-key="giftCardImageKey"
          :subject-name="giftCardRecipientName"
          :donation-cause-id="giftCardDonationCauseId"
          :message="giftCardGreeting"
          :donor-names="giftCardDonorNames"
          @close="preview = false"
          @edit="onPreviewEdit"
        />
        <div v-if="showSummary && donation && donation.paymentSpecification">
            <option-label :title="t('donationGuide.summaryView.donorHeading')" />
            <div class="donation-summary">
              <div class="donation-summary__row">
                <span class="donation-summary__label">{{ t('donorDetails.form.name.label') }}</span>
                <span class="donation-summary__value">{{ donorFullName || t('donorDetails.anonymous') }}</span>
              </div>
            </div>
            <v-divider class="mb-4" />
            <option-label :title="t('donationGuide.summaryView.donationHeading')" class="mb-4" />
            <div class="donation-summary">
              <div v-if="isMemoryGift" class="donation-summary__row">
                <span class="donation-summary__label">{{ t('summary.recipient') }}</span>
                <span class="donation-summary__value donation-summary__value--grey">{{ giftCardRecipientName }}</span>
              </div>
              <div class="donation-summary__row">
                <span class="donation-summary__label">{{ isMemoryGift ? t('summary.charity') : t('summary.recipient') }}</span>
                <span class="donation-summary__value donation-summary__value--grey">{{ recipientName }}</span>
              </div>
              <div class="donation-summary__row">
                <span class="donation-summary__label">{{ t('donationGuide.summaryView.paymentMethod') }}</span>
                <span class="donation-summary__value">{{ paymentMethodLabel }}</span>
              </div>
              <div class="donation-summary__row my-4 mt-10">
                <span class="donation-summary__label">{{ t('donationGuide.payment.summary.yourDonation') }}</span>
                <span class="donation-summary__value">{{ formatCurrency(causeAmount) }}</span>
              </div>
              <div v-if="adminAmount > 0" class="donation-summary__row">
                <span class="donation-summary__label">
                  {{ t('donationGuide.payment.summary.fee') }}
                  <v-tooltip location="top" :open-delay="0" :close-delay="1000" :max-width="300" color="white">
                    <template #activator="{ props }">
                      <v-icon v-bind="props" size="small" class="ml-1 fee-info-icon">mdi-information</v-icon>
                    </template>
                    <span>{{ t('donationDetails.form.adminFee.hint') }}</span>
                  </v-tooltip>
                </span>
                <span class="donation-summary__value">+ {{ formatCurrency(adminAmount) }}</span>
              </div>
              <v-divider class="my-4" />
              <div class="donation-summary__row donation-summary__row--total">
                <span class="donation-summary__label">{{ t('donationGuide.payment.summary.total') }}</span>
                <span class="donation-summary__value">{{ formatCurrency(totalAmount) }}</span>
              </div>
            </div>
        </div>

        <!-- Info about delivery -->
        <div v-if="isMemoryGift" class="delivery-info mt-6">
          <p class="delivery-info__title">{{ t('donationGuide.summaryView.deliveryInfo.title') }}</p>
          <div class="delivery-info__item">
            <v-icon class="delivery-info__icon" size="20">mdi-hand-heart</v-icon>
            <span>{{ t('donationGuide.summaryView.deliveryInfo.donation') }}</span>
          </div>
          <div class="delivery-info__item">
            <v-icon class="delivery-info__icon" size="20">mdi-email-outline</v-icon>
            <span>{{ t('donationGuide.summaryView.deliveryInfo.receipt') }}</span>
          </div>
          <div class="delivery-info__item">
            <v-icon class="delivery-info__icon" size="20">mdi-image-outline</v-icon>
            <span>{{ t('donationGuide.summaryView.deliveryInfo.printed') }}</span>
          </div>
        </div>
        <!-- <v-checkbox
          v-if="!isMemoryGift"
          v-model="acceptPreview"
          data-cy="summary-view-accept-preview-check"
          color="primary"
          :false-value="false"
          :true-value="true"
          :label="t('confirm.form.acceptPreview.alternate')"
          :rules="[rules.requiredCheck(t('validation.rule.acceptPreviewAlternate.label'))]"
        />
        <v-checkbox
          v-else
          v-model="acceptPreview"
          data-cy="summary-view-accept-preview-check"
          color="primary"
          :false-value="false"
          :true-value="true"
          :label="t('confirm.form.acceptPreview.label')"
          :rules="[rules.requiredCheck(t('validation.rule.acceptPreview.label'))]"
        />
        <v-checkbox
          v-if="
            donation && donation.paymentSpecification && donation.paymentSpecification.paymentMethod
          "
          v-model="donation.sharePersonalInfoWithOrganization"
          data-cy="summary-view-share-personal-info-check"
          color="primary"
          :rules="[
            rules.requiredCheck(t('validation.rule.sharePersonalInfoWithOrganization.label')),
          ]"
        >
          <template #label>
            <div>
              {{ t('confirm.form.sharePersonalInfoWithOrganization.label') }}
              <v-tooltip v-model="privacyTooltipOpen" bottom>
                <template #activator="{ props }">
                  <a
                    :href="privacyPolicyUrl"
                    target="_blank"
                    v-bind="props"
                    @click.stop="onPrivacyClick"
                  >
                    {{ t('summary.privacyPolicy.link.text') }}
                  </a>
                </template>
                {{ t('summary.privacyPolicy.link.tooltip') }}
              </v-tooltip>
            </div>
          </template>
        </v-checkbox> -->
      </v-form>
      <v-snackbar v-model="snackbar" color="secondary">
        {{ t('confirm.snackbarText') }}
      </v-snackbar>
      <v-alert
        v-model="submitError"
        border="start"
        type="error"
        colored-border
        elevation="2"
        data-cy="payment-error-alert"
      >
        {{ t('confirm.submitError') }}
        <div v-if="renderError" class="mt-2" style="white-space: pre-wrap; font-size: 12px">
          <strong>Debug:</strong>
          <pre style="max-height: 240px; overflow: auto">{{ renderError }}</pre>
        </div>
      </v-alert>

      <paylink-error-retry-component
        :donation="donation"
        :show="sms.errorDialog"
        :message="sms.errorMessage"
        @to-payment-selection="goToPaymentMethodSelection"
        @to-success="goToSuccessMethodSelection"
      />
    </v-card-text>
    <donation-flow-buttons
      previous-id="summaryViewBackButton"
      next-id="summaryViewNextButton"
      previous-data-cy="summary-view-previous-btn"
      next-data-cy="summary-view-submit-btn"
      :next-label="payText"
      :next-loading="submitting || loadingSendingSms"
      @previous-step="backButton"
      @next-step="submit"
    />
      <v-card-text class="mt-4 policy-text">
        {{ t('legal.policyText.intro') }}
        <a :href="privacyPolicyUrl" target="_blank" rel="noopener" @click="onPrivacyClickLog">{{ t('legal.policyText.privacyPolicyLink') }}</a>.
      </v-card-text>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onErrorCaptured } from 'vue';
import { getCurrencyForLocation } from '@/utils/currency';
import store from '@/store';
import { ENDPOINTS, post, BASE_URL, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { useImageOperations } from '@/composables/useImageOperations';
import { extractDominantColors } from '@/utils/colorExtraction';
import { RequestSession } from '@/interfaces/requestSession';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import PaylinkErrorRetryComponent from '@/components/payment/PaylinkErrorRetryComponent.vue';
import CertificatePreview from '@/components/global/CertificatePreviewComponent.vue';
import OptionLabel from '@/components/OptionLabel.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import { Donation } from '@/interfaces/donation/donation';
import {
  isInvoice,
  isPayLink as isPayLinkFunc,
  isDigitalPayment as isDigitalPaymentFunc,
} from '@/classes/PaymentMethod';
import { Sms } from '@/interfaces/sms';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import { AttributeType } from '@/enums/attributeType';
import { isMemoryGift as isMemoryGiftFunc } from '@/classes/DonationType';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { useSmsOperations } from '@/composables/useSmsOperations';
import matomo from '@/services/matomoService';
import { useLabel } from '@/composables/useLabel';
// validation helpers moved to composable useValidationRules
import { useValidationRules } from '@/composables/useValidationRules';

export default defineComponent({
  components: {
    PaylinkErrorRetryComponent,
    CertificatePreview,
    OptionLabel,
    DonationFlowButtons,
  },
  props: {
    intentText: {
      type: String,
      default: 'confirm.form.acceptReasonMemorial.label',
    },
    step: {
      type: Number,
      default: 0,
    },
    compact: Boolean,
  },
  emits: ['step', 'previousStep', 'nextStep'],
  setup(props, { emit }) {
    const { t } = useLabel();

    const { rules } = useValidationRules(t);
    const { fetchImagesAndCategories } = useImageOperations();
    const { sendPaymentRequestSms, getLatestSmsStatus } = useSmsOperations();

    // Inlined from StepperFlowMixin
    const defaultDonationGuideVersion = ref(DonationGuideVersion.V1);
    const getDonationStepFlowVersion = (): DonationGuideVersion => {
      return store.state.donationGuideVersion || defaultDonationGuideVersion.value;
    };

    // Data
    const valid = ref(false);
    const preview = ref(false);
    const acceptPreview = ref(false);
    const acceptReason = ref(true);
    const paymentMethod = ref(null);
    const previewError = ref(false);
    const submitError = ref(false);
    const submitting = ref(false);
    const snackbar = ref(false);
    const showSummary = ref(false);
    const blankImageId = ref<number | null>(null);
    const dominantColors = ref<string[]>([]);
    const selectedImageUrl = ref('');
    const loadingSendingSms = ref(false);
    const sms = ref({
      errorDialog: false,
      errorMessage: '',
      smsValidationWaitTime: 4000,
    });

    // original behavior: rely on local defensive guards in components for missing fields
    // default to standard action, ensures button has visible text even if setPayText isn't called yet
    const payText = ref(String(t('confirm.pay')));
    const form = ref(null);
    const renderError = ref<string | null>(null);

    onErrorCaptured((err: unknown, instance, info) => {
      window.log?.error('[Guide] summary.render — Summary view render or setup error', {
        message: guideErrorMessage(err),
        info,
        donationGuideVersion: store.state.donationGuideVersion,
        organizationGroupId: store.state.requestSession?.organizationGroup?.id,
        donationId: store.state.donation?.id,
        serialnumber: store.state.donation?.serialnumber,
        currentStep: store.state.currentStep,
      });
      renderError.value = err instanceof Error ? err.stack || err.message : String(err);
      showSummary.value = false;
      submitError.value = true;
      // Stop propagation to avoid noisy Vue global warnings and keep UI consistent
      return false;
    });

    // Computed
    const steps = computed((): typeof stepV1 | typeof stepV2 => {
      return getDonationStepFlowVersion() === DonationGuideVersion.V1 ? stepV1 : stepV2;
    });

    const requestSession = computed((): RequestSession => {
      return store.state.requestSession as RequestSession;
    });

    const donation = computed((): Donation => {
      return store.state.donation as Donation;
    });

    const currentStep = computed(() => {
      return store.state.currentStep;
    });

    // track entering summary step when currentStep changes
    watch(
      currentStep,
      (newVal) => {
        if (
          newVal === stepV1.SUMMARY
        ) {
          matomo.trackEvent('Contibution', 'Viewed', 'Summary Screen')
        }
      },
      { immediate: false }
    );

    const isMemoryGift = computed(() => {
      return !!(
        donation.value &&
        donation.value.donationType &&
        isMemoryGiftFunc(donation.value.donationType.type)
      );
    });

    const currentCurrency = computed(() =>
      getCurrencyForLocation(store.state.userLocation || 'SWEDEN')
    );



    const totalAmount = computed(() => {
      if (!donation.value?.paymentSpecification) return 0;
      const causeAmt = donation.value.paymentSpecification.causeAmount || 0;
      const adminAmt = donation.value.paymentSpecification.adminAmount || 0;
      return causeAmt + adminAmt;
    });

    const causeAmount = computed(() => {
      return donation.value?.paymentSpecification?.causeAmount || 0;
    });

    const adminAmount = computed(() => {
      return donation.value?.paymentSpecification?.adminAmount || 0;
    });

    const donorFullName = computed(() => {
      const d = donation.value?.donor as any;
      if (!d) return '';
      const f = d.firstname || '';
      const l = d.surname || '';
      return `${f} ${l}`.trim();
    });

    const paymentMethodLabel = computed(() => {
      const pm = donation.value?.paymentSpecification?.paymentMethod;
      if (!pm) return '';
      // if payment method provides a translation key use it, otherwise fall back to name
      return pm.nameTranslationKey ? t(pm.nameTranslationKey) : pm.name;
    });

    // organization group name to display as recipient; fall back to requestSession when donation
    // object doesn't yet have the cause (e.g. contribution flow pre-selection)
    const recipientName = computed(() => {
      return (
        donation.value?.donationCause?.organizationGroup?.name ||
        donation.value?.donationCause?.name ||
        requestSession.value.organizationGroup?.name ||
        ''
      );
    });

    const formatCurrency = (amount: number | null) => {
      if (amount === null || amount === undefined) return '';
      const currency = getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    }; 

    const hasAdminPercentage = computed((): boolean => {
      return (
        !!donation.value &&
        !!donation.value.paymentSpecification &&
        !!donation.value.paymentSpecification.adminPercentage
      );
    });

    // Tooltip control for privacy link
    const privacyTooltipOpen = ref(false);
    const onPrivacyClick = () => {
      privacyTooltipOpen.value = false;
    };

    const onPrivacyClickLog = () => {
      matomo.trackEvent('Contribution', 'Clicked', 'Privacy Policy Link');
    };

    // ----- Gift card summary helpers -----

    const giftCardRecipientName = computed(
      () =>
        getAttributeValueById(AttributeType.SUBJECT_NAME) ||
        donation.value?.donationCase?.caseInfo?.name ||
        ''
    );
    const giftCardGreeting = computed(() => getAttributeValueById(AttributeType.GREETING));
    const giftCardDonorNames = computed(() => getAttributeValueById(AttributeType.DONOR_NAME));
    const giftCardImageId = computed(() => donation.value?.donationImageId || null);
    const giftCardImageUrl = computed(() => donation.value?.customCertificateImageUrl || '');
    const giftCardImageKey = computed(() => donation.value?.imageKey || '');
    const giftCardDonationCauseId = computed(
      () =>
        donation.value?.donationCause?.id ||
        donation.value?.donationCase?.defaultDonationCauseIds?.[0] ||
        1
    );

    // Whether the user actually added a theme image (the "No Theme" blank doesn't count)
    const hasGiftCardImage = computed(() => {
      if (giftCardImageUrl.value) return true;
      const id = donation.value?.donationImageId;
      if (!id) return false;
      if (blankImageId.value != null && id === blankImageId.value) return false;
      return true;
    });

    // "Image · greeting · sender" — reflects what the user added, "Blank" if nothing
    const giftCardContents = computed(() => {
      const parts: string[] = [];
      if (hasGiftCardImage.value) parts.push(t('summary.giftCard.image') as string);
      if (giftCardGreeting.value?.trim()) parts.push(t('summary.giftCard.greeting') as string);
      if (giftCardDonorNames.value?.trim()) parts.push(t('summary.giftCard.sender') as string);
      if (parts.length === 0) return t('summary.giftCard.blank') as string;
      const joined = parts.join(' · ');
      return joined.charAt(0).toUpperCase() + joined.slice(1);
    });

    // Background for the gift card swatch.
    // - No image (toggled off / "No Theme" blank): plain grey → white gradient.
    // - With image: gradient built from the image's dominant colours.
    const swatchStyle = computed(() => {
      if (!hasGiftCardImage.value || !dominantColors.value.length) {
        return { background: 'linear-gradient(135deg, #d1d5db 0%, #ffffff 100%)' };
      }
      const colors = dominantColors.value;
      const stops = colors.length === 1 ? `${colors[0]}, ${colors[0]}` : colors.join(', ');
      return { background: `linear-gradient(135deg, ${stops})` };
    });

    // Determine the "No Theme" image id so we can tell a blank card from a themed one.
    // Must mirror CertificateDetails.fetchImages exactly, otherwise the blank id won't
    // match donation.donationImageId and a blank card is mistaken for a themed one.
    const resolveBlankImageId = async () => {
      try {
        const orgGroupId =
          donation.value?.donationCause?.organizationGroup?.id ||
          requestSession.value?.organizationGroup?.id;
        const result = await fetchImagesAndCategories(orgGroupId);
        const all = (result.images || []) as Array<{
          id: number;
          url: string;
          donationImageCategory?: { name?: string };
        }>;

        const noTheme = all.find((img) => img.donationImageCategory?.name === 'No Theme');
        if (noTheme?.id != null) {
          blankImageId.value = noTheme.id;
        } else {
          // Fall back to the first visible themed image, same ordering as CertificateDetails
          const categoryOrder = ['Candles', 'Flowers', 'Nature', 'Landscape'];
          const visible = all
            .filter((img) => categoryOrder.includes(img.donationImageCategory?.name ?? ''))
            .sort(
              (a, b) =>
                categoryOrder.indexOf(a.donationImageCategory?.name ?? '') -
                categoryOrder.indexOf(b.donationImageCategory?.name ?? '')
            );
          blankImageId.value = visible.length > 0 ? visible[0].id : null;
        }

        // Resolve the raw URL of the user's selected image so the swatch gradient
        // is built from the image itself, not the composed certificate.
        const customUrl = donation.value?.customCertificateImageUrl;
        if (customUrl) {
          selectedImageUrl.value = customUrl.startsWith('http') || customUrl.startsWith('data:')
            ? customUrl
            : `${BASE_URL}${customUrl}`;
        } else {
          const selected = all.find((img) => img.id === donation.value?.donationImageId);
          if (selected?.url) {
            const base = !selected.url.startsWith('http') ? BASE_URL : '';
            selectedImageUrl.value = `${base}${selected.url}?compression=true`;
          } else {
            selectedImageUrl.value = '';
          }
        }
      } catch {
        blankImageId.value = null;
        selectedImageUrl.value = '';
      }
    };

    const loadDominantColors = async () => {
      // Only colour the swatch from a real theme image — a blank card keeps the grey → white gradient
      if (!hasGiftCardImage.value || !selectedImageUrl.value) return;
      const colors = await extractDominantColors(selectedImageUrl.value, 3);
      if (colors.length) {
        dominantColors.value = colors;
      }
    };

    const openPreview = () => {
      if (previewError.value) return;
      preview.value = true;
      matomo.trackEvent('checkout', 'gift_card_preview_clicked', 'donations_checkout_gift_card_preview_clicked');
    };

    const editGiftCard = () => {
      matomo.trackEvent('checkout', 'gift_card_edit_clicked', 'donations_checkout_gift_card_edit_clicked');
      emit('step', (steps.value as typeof stepV2).CERTIFICATE_DETAILS);
    };

    const onPreviewEdit = () => {
      preview.value = false;
      editGiftCard();
    };

    // Watch
    // Restore original behavior: show summary when the current step is SUMMARY; avoid added gating/retries.
    watch(
      currentStep,
      (val) => {
        showSummary.value = false;
        if (val === steps.value.SUMMARY) {
          matomo.trackEvent('checkout', 'viewed', 'donations_checkout_viewed');
          if (isMemoryGift.value) {
            // Clear any colours from a previous visit so the swatch starts neutral
            dominantColors.value = [];

            // Resolve the blank-image id first so hasGiftCardImage is accurate
            // before we decide whether to colour the swatch from the image
            resolveBlankImageId()
              .then(() => {
                loadDominantColors();
              })
              .catch(() => {
                selectedImageUrl.value = '';
              });
          }
          showSummary.value = true;
          try {
            setPayText();
          } catch (err) {
            window.log?.debug('[Guide] summary.setPayText — Failed to set pay button text', { message: guideErrorMessage(err) });
          }
          // clear any previous render error now that we have fresh data
          renderError.value = null;
        }
      },
      { immediate: true }
    );

    // Methods
    const goToPaymentMethodSelection = (): void => {
      emit('step', steps.value.PAYMENT);
      sms.value.errorDialog = false;
    };

    const getAttributeValueById = (id: number): string => {
      if (!donation.value || !Array.isArray(donation.value.attributeValues)) return '';
      const attributeValue = donation.value.attributeValues.find((a) => a.attributeId === id);
      return attributeValue ? attributeValue.value : '';
    };

    const goToSuccessMethodSelection = (): void => {
      emit('step', steps.value.SUCCESS);
      sms.value.errorDialog = false;
    };

    const backButton = (): void => {
      matomo.trackEvent('Contribution', 'Clicked', 'Previous')
      emit('previousStep');
      matomo.trackEvent('checkout', 'previous_clicked', 'donations_checkout_previous_clicked');
    };

    const submit = (): void => {
      matomo.trackEvent('Contribution', 'Clicked', 'Pay Button')
      if (!(form.value as any)?.validate()) {
        return;
      }

      submitting.value = true;
      submitError.value = false;

      const timeout = setTimeout(() => (snackbar.value = true), 500);

      // Ensure sharePersonalInfoWithOrganization is always true before submitting.
      // In Vue 2 this was a required checkbox (always true); the checkbox is currently
      // commented out in Vue 3 so we force it to true here to match that behaviour.
      if (!donation.value.sharePersonalInfoWithOrganization) {
        store.commit('updateDonation', { ...donation.value, sharePersonalInfoWithOrganization: true });
      }

      // Last-resort donationCause fallback: if the cause is still null at submit time
      // (e.g. requestSession loaded after type selection, or autocomplete binding failed),
      // pick the first active cause from the session.
      if (!donation.value.donationCause) {
        const orgGroup = store.state.requestSession?.organizationGroup;
        const activeCauses = (orgGroup?.donationCauses ?? []).filter((c: any) => c.active);
        if (activeCauses.length > 0) {
          const { organizationGroup: _o1, pingPaymentsMerchant: _o2, pennybridgeCause: _o3, pennybridgeId: _o4, entityType: _o5, createdAt: _o6, updatedAt: _o7, ...causeWithoutOrg } = activeCauses[0] as any;
          store.commit('updateDonation', { ...donation.value, donationCause: causeWithoutOrg });
        }
      }

      // Ensure adminPercentage and adminAmount are never null before submitting.
      // The backend does its own DB lookup when adminPercentage is null, and NPEs if no
      // admin fee record is configured for this org group. Sending 0 tells it no fee applies.
      if (donation.value.paymentSpecification && donation.value.paymentSpecification.adminPercentage == null) {
        store.commit('updateDonation', {
          ...donation.value,
          paymentSpecification: {
            ...donation.value.paymentSpecification,
            adminPercentage: 0,
            adminAmount: donation.value.paymentSpecification.adminAmount ?? 0,
          },
        });
      }

      let redirectingToPaymentProvider = false;
      const checkoutAmounts = donation.value.paymentSpecification
        ? {
            causeAmount: donation.value.paymentSpecification.causeAmount,
            adminAmount: donation.value.paymentSpecification.adminAmount ?? 0,
            adminPercentage: donation.value.paymentSpecification.adminPercentage,
            originalCauseAmount: donation.value.paymentSpecification.originalCauseAmount,
          }
        : null;

      post(ENDPOINTS.DONATION, { ...donation.value, sharePersonalInfoWithOrganization: true })
        .then((data) => {
          store.commit('updateRequestSession', data.requestSession);
          matomo.trackEvent('checkout', 'proceeded_to_payment', 'donations_proceeded_to_payment');

          const donationForStore =
            checkoutAmounts && data.donation.paymentSpecification
              ? {
                  ...data.donation,
                  paymentSpecification: {
                    ...data.donation.paymentSpecification,
                    ...checkoutAmounts,
                  },
                }
              : data.donation;

          // Paylink first so we never fall through to the legacy payment step.
          if (isPayLink()) {
            store.commit('updateDonation', data.donation);
            loadingSendingSms.value = true;
            const mobile = donation.value.donor?.mobileNumber || '';
            sendPaymentRequestSms(donation.value.serialnumber, mobile)
              .then(() => {
                setTimeout(() => {
                  getLatestSmsStatus(donation.value.serialnumber)
                    .then((smsData: Sms) => {
                      loadingSendingSms.value = false;

                      if (smsData.errorMessage && smsData.errorMessage.length > 0) {
                        window.log?.error('[Guide] checkout.paylinkSms — Paylink SMS reported an error', {
                          serialnumber: donation.value?.serialnumber,
                          smsErrorMessage: smsData.errorMessage,
                          organizationGroupId: store.state.requestSession?.organizationGroup?.id,
                        });
                        sms.value.errorDialog = true;
                        sms.value.errorMessage = smsData.errorMessage;
                      } else {
                        const snackbarData: Snackbar = {
                          active: true,
                          color: 'success',
                          text: t('payLinkSend.messages.payLinkSend.success') as string,
                          timeout: 8000,
                          action: null,
                        };
                        store.commit('setSnackbar', snackbarData);

                        emit('step', steps.value.SUCCESS);
                      }
                    })
                    .catch((error) => {
                      window.log?.error('[Guide] checkout.paylinkSmsStatus — Failed to verify paylink SMS status', {
                        message: guideErrorMessage(error),
                        httpStatus: guideHttpStatus(error),
                        serialnumber: donation.value?.serialnumber,
                        organizationGroupId: store.state.requestSession?.organizationGroup?.id,
                      });
                      loadingSendingSms.value = false;
                      sms.value.errorDialog = true;
                      sms.value.errorMessage = t(
                        'payLinkSend.messages.payLinkSend.probableErrorCheckPhoneNumber'
                      ) as string;
                    });
                }, sms.value.smsValidationWaitTime);
              })
              .catch((error) => {
                window.log?.error('[Guide] checkout.paylinkSmsStatus — Failed to verify paylink SMS status', {
                  message: guideErrorMessage(error),
                  httpStatus: guideHttpStatus(error),
                  serialnumber: donation.value?.serialnumber,
                  organizationGroupId: store.state.requestSession?.organizationGroup?.id,
                });
                loadingSendingSms.value = false;
                sms.value.errorDialog = true;
                sms.value.errorMessage = t(
                  'payLinkSend.messages.payLinkSend.probableErrorCheckPhoneNumber'
                ) as string;
              });
          } else if (isInvoicePayment()) {
            store.commit('updateDonation', data.donation);
            // go directly to the invoice step, because it is already 'paid'
            emit('step', steps.value.SUCCESS);
          } else if (isDigitalPayment()) {
            // Keep the checkout amounts visible while redirecting — the backend
            // recalculates admin fees on create, which can briefly flash a fee line
            redirectingToPaymentProvider = true;
            store.commit('updateDonation', donationForStore);
            matomo.navigateAfterTrack(data.requestSession.paymentResponse.redirectUrl);
          } else {
            store.commit('updateDonation', data.donation);
          }
        })
        .catch((error) => {
          window.log?.error('[Guide] checkout.createDonation — Payment failed during checkout', {
            message: guideErrorMessage(error),
            httpStatus: guideHttpStatus(error),
            paymentMethodType: donation.value?.paymentSpecification?.paymentMethod?.type,
            donationId: donation.value?.id,
            serialnumber: donation.value?.serialnumber,
            organizationGroupId: store.state.requestSession?.organizationGroup?.id,
          });
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'Payment failed. Please try again later.',
          });
          submitError.value = true;
          matomo.trackEvent('checkout', 'payment_error', 'donations_checkout_payment_error');
        })
        .finally(() => {
          clearTimeout(timeout);
          if (!redirectingToPaymentProvider) {
            submitting.value = false;
          }
        });
    };

    const isInvoicePayment = (): boolean => {
      return !!(
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        isInvoice(donation.value.paymentSpecification.paymentMethod.type)
      );
    };

    const isPayLink = (): boolean => {
      return !!(
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        isPayLinkFunc(donation.value.paymentSpecification.paymentMethod.type)
      );
    };

    const isDigitalPayment = (): boolean => {
      return !!(
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        isDigitalPaymentFunc(donation.value.paymentSpecification.paymentMethod.type)
      );
    };

    const isDirectPaymentAvailable = (): boolean => {
      return !!(
        requestSession.value.organizationGroup &&
        requestSession.value.organizationGroup.swedbankPaySettings &&
        requestSession.value.organizationGroup.swedbankPaySettings.id &&
        requestSession.value.organizationGroup.swedbankPaySettings.id > 0
      );
    };

    const setPayText = (): void => {
      try {
        if (isInvoicePayment()) {
          payText.value = String(t('confirm.invoice'));
        } else if (isPayLink()) {
          payText.value = String(t('confirm.paylink'));
        } else {
          payText.value = String(t('confirm.pay'));
        }
      } catch (err) {
        // Fallback to safe default if translations or checks fail
        window.log?.debug('[Guide] summary.setPayText — Failed to set pay button text', { message: guideErrorMessage(err) });
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Failed to set payment button text',
        });
        payText.value = String(t('confirm.pay'));
      }
    };

    const privacyPolicyUrl = computed((): string => {
      const location = store.state.userLocation || 'SWEDEN';
      let langCode = 'se';
      if (location === 'NORWAY') langCode = 'no';
      else if (location === 'GERMANY') langCode = 'de';
      return `${import.meta.env.BASE_URL}privacy/rapid_data_privacy_policy_${langCode}.pdf`;
    });
    
    const termsUrl = computed(() => {
      const location = store.state.userLocation || 'SWEDEN';
      let langCode = 'se';
      if (location === 'NORWAY') langCode = 'no';
      else if (location === 'GERMANY') langCode = 'de';
      return `${import.meta.env.BASE_URL}privacy/rapid_data_privacy_policy_${langCode}.pdf`;
    });

    return {
      valid,
      preview,
      acceptPreview,
      acceptReason,
      paymentMethod,
      previewError,
      submitError,
      submitting,
      snackbar,
      showSummary,
      loadingSendingSms,
      sms,
      payText,
      form,
      renderError,
      rules,
      steps,
      requestSession,
      donation,
      currentStep,
      isMemoryGift,
      hasAdminPercentage,
      currentCurrency,
      goToPaymentMethodSelection,
      getAttributeValueById,
      goToSuccessMethodSelection,
      backButton,
      submit,
      isInvoicePayment,
      isPayLink,
      isDigitalPayment,
      isDirectPaymentAvailable,
      setPayText,
      t,
      totalAmount,
      causeAmount,
      adminAmount,
      donorFullName,
      paymentMethodLabel,
      recipientName,
      formatCurrency,
      giftCardRecipientName,
      giftCardGreeting,
      giftCardDonorNames,
      giftCardImageId,
      giftCardImageUrl,
      giftCardImageKey,
      giftCardDonationCauseId,
      giftCardContents,
      swatchStyle,
      openPreview,
      editGiftCard,
      onPreviewEdit,
      privacyTooltipOpen,
      onPrivacyClick,
      onPrivacyClickLog,
      privacyPolicyUrl,
      termsUrl,
    };
  },
});
</script>
<style scoped lang="scss">

.donation-summary__value {
  margin-left: 10px;
}


.donation-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.summary-view .v-card-text,
.summary-view .v-card-title,
.summary-view .v-card-subtitle {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.table td:not(:first-child) {
  width: 99%;
}

.popup {
  margin: 12px;
}

.donation-summary {
  padding: 16px 0;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }

    &--total {
      margin-top: 4px;
    }
  }

  &__label {
    display: flex;
    align-items: center;
    color: #4b5563;
    font-size: 14px;
    font-weight: 700;
  }
}

.donation-flow__option-title {
  font-weight: 700;
  font-size: 19px;
  color: #6b7280 !important;
}

/* "Your gift card" preview card */
.gift-card-summary {
  &__card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background-color: #fff;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: #d1d5db;
      box-shadow: 0 2px 8px rgba(17, 25, 40, 0.06);
      outline: none;
    }
  }

  &__swatch {
    position: relative;
    flex-shrink: 0;
    width: 40px;
    height: 56px;
    border-radius: 4px;
    overflow: hidden;
    background: linear-gradient(135deg, #d1d5db, #bcae95, #e8e0d2);
  }

  /* Lighter inner "label" box near the bottom of the swatch */
  &__swatch-inner {
    position: absolute;
    left: 14%;
    right: 12%;
    bottom: 10%;
    height: 28%;
    border-radius: 2px;
    background: #d1d5db;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 16px;
    color: #1f2a37;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__contents {
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    color: #6b7280;
    margin-top: 2px;
  }

  &__actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__action {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #047481;

    &:hover,
    &:focus-visible {
      color: #035c66;
      outline: none;
    }
  }

  &__action-divider {
    color: #d1d5db;
    font-weight: 400;
  }
}

.delivery-info {
  border-radius: 6px;
  padding: 24px 28px;
  background-color: #e6f5f5;

  &__title {
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 16px;
    color: #03543f !important;
    margin-bottom: 20px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 14px;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    color: #03543f;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 20px;
    height: 18px;
    color: #03543f;
  }
}
</style>
